import React, { useState, useRef, useEffect } from 'react';
import './ActiveTrader.css';

const fN = (n, d=2) => n!=null&&!isNaN(n)?Number(n).toFixed(d):'--';
const PROXY = 'https://corsproxy.io/?';

function getSession(){
  const et=new Date(new Date().toLocaleString('en-US',{timeZone:'America/New_York'}));
  const m=et.getHours()*60+et.getMinutes(),d=et.getDay();
  if(d===0||d===6)return'closed';
  if(m>=240&&m<570)return'premarket';
  if(m>=570&&m<960)return'market';
  if(m>=960&&m<1200)return'afterhours';
  return'closed';
}

function genL2(base){
  const mms=['NSDQ','ARCA','BATS','EDGX','NYSE'];
  return {
    bids:Array.from({length:8},(_,i)=>({price:+(base-0.01*(i+1)).toFixed(2),size:Math.floor(Math.random()*4000+200),mm:mms[Math.floor(Math.random()*5)]})),
    asks:Array.from({length:8},(_,i)=>({price:+(base+0.01*(i+1)).toFixed(2),size:Math.floor(Math.random()*4000+200),mm:mms[Math.floor(Math.random()*5)]})),
  };
}

async function fetchQuote(sym){
  try{
    const url=`https://query1.finance.yahoo.com/v8/finance/chart/${sym}?interval=1m&range=1d&includePrePost=true`;
    const r=await fetch(PROXY+encodeURIComponent(url));
    const d=await r.json();
    const meta=d?.chart?.result?.[0]?.meta;
    if(!meta?.regularMarketPrice)return null;
    const last=meta.regularMarketPrice;
    const pc=meta.previousClose||meta.chartPreviousClose||last;
    const chg=+(last-pc).toFixed(2);
    const pct=+((chg/pc)*100).toFixed(2);
    const vol=meta.regularMarketVolume||0;
    return{
      last,chg,pct,
      open:meta.regularMarketOpen||last,
      high:meta.regularMarketDayHigh||last,
      low:meta.regularMarketDayLow||last,
      vol:vol>=1e9?(vol/1e9).toFixed(1)+'B':vol>=1e6?(vol/1e6).toFixed(1)+'M':vol>=1e3?(vol/1e3).toFixed(0)+'K':''+vol,
    };
  }catch{return null;}
}

const FALLBACK={last:0,chg:0,pct:0,open:0,high:0,low:0,vol:'--'};

const SESSION_STYLES={
  premarket: {bg:'#0f1a0a',border:'#854d0e',color:'#f59e0b',label:'◑ PRE-MARKET',sub:'4:00am – 9:30am ET'},
  afterhours:{bg:'#0d0a1e',border:'#5b21b6',color:'#a78bfa',label:'◑ AFTER HOURS',sub:'4:00pm – 8:00pm ET'},
  closed:    {bg:'#0d1117',border:'#21262d',color:'#484f58',label:'◌ MARKET CLOSED',sub:'Opens 4:00am ET'},
  market:    {bg:'#0a1a0a',border:'#166534',color:'#22c55e',label:'● MARKET OPEN',sub:'9:30am – 4:00pm ET'},
};

const HOTKEY_DEFAULTS={
  'F1':{action:'BUY_BID',  label:'Buy Bid',   color:'#22c55e'},
  'F2':{action:'BUY_MKT',  label:'Buy Mkt',   color:'#16a34a'},
  'F3':{action:'BUY_ASK',  label:'Buy Ask',   color:'#15803d'},
  'F4':{action:'SELL_BID', label:'Sell Bid',  color:'#ef4444'},
  'F5':{action:'SELL_MKT', label:'Sell Mkt',  color:'#dc2626'},
  'F6':{action:'SELL_ASK', label:'Sell Ask',  color:'#b91c1c'},
  'F9':{action:'CANCEL',   label:'Cancel All',color:'#f59e0b'},
  'Escape':{action:'CANCEL',label:'Cancel',   color:'#f59e0b'},
};

export default function GrooveTrader({ sym='AAPL', onClose }){
  const [symbol,setSymbol]=useState(sym.toUpperCase());
  const [symInput,setSymInput]=useState(sym.toUpperCase());
  const [quote,setQuote]=useState(FALLBACK);
  const [l2,setL2]=useState(()=>genL2(100));
  const [loading,setLoading]=useState(false);
  const [side,setSide]=useState('BUY');
  const [orderType,setOrderType]=useState('LMT');
  const [qty,setQty]=useState(100);
  const [limitPrice,setLimitPrice]=useState('');
  const [stopPrice,setStopPrice]=useState('');
  const [tif,setTif]=useState('DAY');
  const [bracket,setBracket]=useState(false);
  const [slPrice,setSlPrice]=useState('');
  const [tpPrice,setTpPrice]=useState('');
  const [orders,setOrders]=useState([]);
  const [positions,setPositions]=useState([]);
  const [fills,setFills]=useState([]);
  const [activeTab,setActiveTab]=useState('exec');
  const [hotkeys,setHotkeys]=useState(HOTKEY_DEFAULTS);
  const [editingHK,setEditingHK]=useState(null);
  const [showHK,setShowHK]=useState(false);
  const [toast,setToast]=useState(null);
  const [acctBal]=useState(100000);
  const [dragging,setDragging]=useState(false);
  const [pos,setPos]=useState({x:Math.max(0,window.innerWidth/2-280),y:60});
  const dragRef=useRef(null);
  const panelRef=useRef(null);

  const session=getSession();
  const sessionStyle=SESSION_STYLES[session];
  const bid=+(l2.bids[0]?.price||quote.last||0).toFixed(2);
  const ask=+(l2.asks[0]?.price||quote.last||0).toFixed(2);
  const spread=+(ask-bid).toFixed(2);
  const isUp=quote.chg>=0;
  const curPos=positions.find(p=>p.sym===symbol);
  const unrealized=curPos?(quote.last-curPos.avg)*curPos.shares:0;
  const buyPower=acctBal-positions.reduce((s,p)=>s+Math.abs(p.shares*p.avg),0);
  const estCost=qty*(parseFloat(orderType==='MKT'?(side==='BUY'?ask:bid):limitPrice)||0);

  // Load quote
  async function loadQuote(s){
    setLoading(true);
    const q=await fetchQuote(s);
    const data=q||FALLBACK;
    setQuote(data);
    setL2(genL2(data.last||100));
    setLimitPrice(fN(side==='BUY'?+(data.last+0.01).toFixed(2):+(data.last-0.01).toFixed(2)));
    setStopPrice(fN(side==='BUY'?+(data.last*0.98).toFixed(2):+(data.last*1.02).toFixed(2)));
    setLoading(false);
  }

  useEffect(()=>{loadQuote(symbol);},[symbol]); // eslint-disable-line
  useEffect(()=>{const i=setInterval(()=>loadQuote(symbol),30000);return()=>clearInterval(i);},[symbol]); // eslint-disable-line
  // Refresh L2 every 2s
  useEffect(()=>{
    const i=setInterval(()=>setL2(p=>({
      bids:p.bids.map(b=>({...b,size:Math.max(100,b.size+Math.floor((Math.random()-0.5)*500))})),
      asks:p.asks.map(a=>({...a,size:Math.max(100,a.size+Math.floor((Math.random()-0.5)*500))})),
    })),2000);
    return()=>clearInterval(i);
  },[]);

  // Hotkeys
  useEffect(()=>{
    function onKey(e){
      if(e.target.tagName==='INPUT')return;
      const hk=hotkeys[e.key];
      if(!hk)return;
      e.preventDefault();
      execHotkey(hk.action);
    }
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[hotkeys,side,qty,bid,ask,limitPrice]); // eslint-disable-line

  function execHotkey(action){
    if(action==='BUY_BID') placeOrder('BUY','LMT',bid);
    else if(action==='BUY_MKT')  placeOrder('BUY','MKT',ask);
    else if(action==='BUY_ASK')  placeOrder('BUY','LMT',ask);
    else if(action==='SELL_BID') placeOrder('SELL','LMT',bid);
    else if(action==='SELL_MKT') placeOrder('SELL','MKT',bid);
    else if(action==='SELL_ASK') placeOrder('SELL','LMT',ask);
    else if(action==='CANCEL')   cancelAll();
  }

  function placeOrder(s,type,px){
    const price=type==='MKT'?(s==='BUY'?ask:bid):px||parseFloat(limitPrice)||(s==='BUY'?ask:bid);
    const now=new Date().toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit'});
    const id='GT-'+Math.random().toString(36).substr(2,6).toUpperCase();
    const filled=type==='MKT';
    const order={id,sym:symbol,side:s,type,qty,price,tif,status:filled?'FILLED':'WORKING',time:now};
    setOrders(prev=>[order,...prev.slice(0,29)]);
    if(filled){
      setFills(prev=>[{...order,fillPx:price,fillTime:now},...prev.slice(0,19)]);
      setPositions(prev=>{
        const ex=prev.find(p=>p.sym===symbol);
        const q=qty;
        if(ex){
          const ns=s==='BUY'?ex.shares+q:ex.shares-q;
          if(ns===0)return prev.filter(p=>p.sym!==symbol);
          const newAvg=s==='BUY'?((ex.avg*ex.shares)+(price*q))/(ex.shares+q):ex.avg;
          return prev.map(p=>p.sym===symbol?{...p,shares:ns,avg:newAvg}:p);
        }
        return[...prev,{sym:symbol,shares:s==='BUY'?q:-q,avg:price}];
      });
      showToast(`${s==='BUY'?'✅ BOT':'🔴 SLD'} ${qty} ${symbol} @ $${fN(price)}`,'success');
    }else{
      showToast(`📋 ${s} ${qty} ${symbol} LMT $${fN(price)} working`,'info');
    }
  }

  function cancelAll(){
    setOrders(p=>p.map(o=>o.status==='WORKING'?{...o,status:'CANCELLED'}:o));
    showToast('❌ All working orders cancelled','warn');
  }

  function closePosition(){
    if(!curPos)return;
    placeOrder(curPos.shares>0?'SELL':'BUY','MKT',null);
  }

  function showToast(msg,type){
    setToast({msg,type});
    setTimeout(()=>setToast(null),3000);
  }

  // Drag
  function startDrag(e){
    if(e.target.closest('.gt-no-drag'))return;
    dragRef.current={sx:e.clientX-pos.x,sy:e.clientY-pos.y};
    setDragging(true);
  }
  useEffect(()=>{
    if(!dragging)return;
    const mv=e=>{if(dragRef.current)setPos({x:e.clientX-dragRef.current.sx,y:e.clientY-dragRef.current.sy});};
    const up=()=>setDragging(false);
    window.addEventListener('mousemove',mv);
    window.addEventListener('mouseup',up);
    return()=>{window.removeEventListener('mousemove',mv);window.removeEventListener('mouseup',up);};
  },[dragging]);

  const workingOrders=orders.filter(o=>o.status==='WORKING');
  const todayPnL=fills.reduce((s,f)=>s+(f.side==='SELL'?f.fillPx*f.qty:-f.fillPx*f.qty),0);

  return(
    <div ref={panelRef} className="gt-panel" style={{left:pos.x,top:pos.y,cursor:dragging?'grabbing':'default'}} onMouseDown={startDrag}>

      {/* ── TITLEBAR ── */}
      <div className="gt-titlebar">
        <div className="gt-title-left">
          <div className="gt-logo">GT</div>
          <div>
            <div className="gt-title-name">Groove Trader</div>
            <div className="gt-title-sub">Paper Trading Mode</div>
          </div>
        </div>
        <div className="gt-title-right gt-no-drag">
          <button className={`gt-hk-toggle ${showHK?'active':''}`} onClick={()=>setShowHK(s=>!s)}>⌨ Hotkeys</button>
          <button className="gt-close-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* ── SESSION BANNER ── */}
      <div className="gt-session" style={{background:sessionStyle.bg,borderBottom:`1px solid ${sessionStyle.border}`}}>
        <span style={{color:sessionStyle.color,fontWeight:700,fontSize:9}}>{sessionStyle.label}</span>
        <span style={{color:sessionStyle.border,fontSize:9,marginLeft:8}}>{sessionStyle.sub}</span>
        <span style={{marginLeft:'auto',fontSize:9,color:'#30363d'}}>Extended Hours Active</span>
      </div>

      {/* ── SYMBOL + QUOTE ── */}
      <div className="gt-quote-bar gt-no-drag">
        <div className="gt-sym-wrap">
          <input
            className="gt-sym-input"
            value={symInput}
            onChange={e=>setSymInput(e.target.value.toUpperCase())}
            onKeyDown={e=>{if(e.key==='Enter'){setSymbol(symInput);}}}
            maxLength={6}
            placeholder="SYM"
          />
          <button className="gt-sym-go" onClick={()=>setSymbol(symInput)}>GO</button>
        </div>
        {loading
          ? <div className="gt-loading">⟳ Loading...</div>
          : <div className="gt-quote-right">
              <div className="gt-last-price" style={{color:isUp?'#22c55e':'#ef4444'}}>${fN(quote.last)}</div>
              <div className="gt-chg" style={{color:isUp?'#22c55e':'#ef4444'}}>{isUp?'+':''}{fN(quote.chg)} ({isUp?'+':''}{fN(quote.pct)}%)</div>
            </div>
        }
      </div>

      {/* ── OHLCV ── */}
      <div className="gt-ohlcv gt-no-drag">
        {[['O',quote.open],['H',quote.high,'#22c55e'],['L',quote.low,'#ef4444'],['C',quote.last]].map(([l,v,c])=>(
          <div key={l} className="gt-ohlcv-item">
            <span className="gt-ohlcv-label">{l}</span>
            <span style={{color:c||'#e6edf3'}}>{fN(v)}</span>
          </div>
        ))}
        <div className="gt-ohlcv-item"><span className="gt-ohlcv-label">VOL</span><span style={{color:'#60a5fa'}}>{quote.vol}</span></div>
        <div className="gt-ohlcv-item"><span className="gt-ohlcv-label">SPD</span><span style={{color:'#f59e0b'}}>${fN(spread)}</span></div>
      </div>

      {/* ── HOTKEY BAR ── */}
      <div className="gt-hk-bar gt-no-drag">
        {Object.entries(hotkeys).filter(([k])=>k.startsWith('F')).map(([key,hk])=>(
          <button
            key={key}
            className="gt-hk-btn"
            style={{borderColor:hk.color+'66',color:hk.color}}
            onClick={()=>execHotkey(hk.action)}
            title={`${key}: ${hk.label}`}
          >
            <span className="gt-hk-key">{key}</span>
            <span className="gt-hk-label">{hk.label}</span>
          </button>
        ))}
      </div>

      {/* ── HOTKEY EDITOR ── */}
      {showHK&&(
        <div className="gt-hk-editor gt-no-drag">
          <div className="gt-hk-editor-title">Customize Hotkeys</div>
          <div className="gt-hk-editor-grid">
            {Object.entries(hotkeys).map(([key,hk])=>(
              <div key={key} className={`gt-hk-editor-row ${editingHK===key?'editing':''}`} onClick={()=>setEditingHK(editingHK===key?null:key)}>
                <span className="gt-hk-editor-key">{key}</span>
                <span style={{color:hk.color,fontSize:10}}>{hk.label}</span>
                {editingHK===key&&(
                  <select className="gt-hk-select" value={hk.action} onChange={e=>setHotkeys(p=>({...p,[key]:{...p[key],action:e.target.value,label:e.target.options[e.target.selectedIndex].text}}))}>
                    {['BUY_BID','BUY_MKT','BUY_ASK','SELL_BID','SELL_MKT','SELL_ASK','CANCEL'].map(a=>(
                      <option key={a} value={a}>{a.replace('_',' ')}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
          <div style={{fontSize:9,color:'#30363d',marginTop:4}}>Click a key to reassign its action · Escape always cancels all orders</div>
        </div>
      )}

      <div className="gt-body gt-no-drag">
        {/* ── LEFT: LEVEL 2 ── */}
        <div className="gt-l2-panel">
          <div className="gt-l2-title">Level 2</div>
          <div className="gt-l2-header">
            <span>MM</span><span>Bid Sz</span><span>Bid</span><span>Ask</span><span>Ask Sz</span><span>MM</span>
          </div>
          <div className="gt-l2-rows">
            {l2.bids.map((b,i)=>{
              const a=l2.asks[i];
              const maxSz=Math.max(...l2.bids.map(x=>x.size),...l2.asks.map(x=>x.size));
              return(
                <div key={i} className="gt-l2-row">
                  <span className="gt-l2-mm">{b.mm}</span>
                  <span className="gt-l2-sz bid-sz" style={{width:`${(b.size/maxSz)*60}%`}}>{b.size.toLocaleString()}</span>
                  <span className="gt-l2-price bid" onClick={()=>{setLimitPrice(fN(b.price));setSide('BUY');}}>{fN(b.price)}</span>
                  {a&&<span className="gt-l2-price ask" onClick={()=>{setLimitPrice(fN(a.price));setSide('SELL');}}>{fN(a.price)}</span>}
                  {a&&<span className="gt-l2-sz ask-sz" style={{width:`${(a.size/maxSz)*60}%`}}>{a.size.toLocaleString()}</span>}
                  {a&&<span className="gt-l2-mm">{a.mm}</span>}
                </div>
              );
            })}
          </div>
          <div className="gt-spread-bar">
            <span style={{color:'#22c55e'}}>Bid ${fN(bid)}</span>
            <span style={{color:'#484f58'}}>Spread ${fN(spread)}</span>
            <span style={{color:'#ef4444'}}>Ask ${fN(ask)}</span>
          </div>
        </div>

        {/* ── RIGHT: ORDER ENTRY ── */}
        <div className="gt-order-panel">

          {/* TABS */}
          <div className="gt-tabs">
            {[['exec','Order'],['positions','Positions'],['orders','Activity']].map(([id,label])=>(
              <div key={id} className={`gt-tab ${activeTab===id?'active':''}`} onClick={()=>setActiveTab(id)}>{label}</div>
            ))}
          </div>

          {activeTab==='exec'&&(
            <>
              {/* BUY / SELL */}
              <div className="gt-side-btns">
                <button className={`gt-side-btn buy ${side==='BUY'?'active':''}`} onClick={()=>setSide('BUY')}>BUY</button>
                <button className={`gt-side-btn sell ${side==='SELL'?'active':''}`} onClick={()=>setSide('SELL')}>SELL</button>
              </div>

              {/* ORDER TYPE */}
              <div className="gt-field-row">
                <span className="gt-label">Type</span>
                <div className="gt-type-btns">
                  {['MKT','LMT','STP','STP LMT'].map(t=>(
                    <button key={t} className={`gt-type-btn ${orderType===t?'active':''}`} onClick={()=>setOrderType(t)}>{t}</button>
                  ))}
                </div>
              </div>

              {/* QTY */}
              <div className="gt-field-row">
                <span className="gt-label">Qty</span>
                <div className="gt-qty-row">
                  <button className="gt-qty-arrow" onClick={()=>setQty(q=>Math.max(1,q-1))}>−</button>
                  <input className="gt-input" type="number" value={qty} onChange={e=>setQty(Math.max(1,parseInt(e.target.value)||1))} style={{width:64,textAlign:'center'}}/>
                  <button className="gt-qty-arrow" onClick={()=>setQty(q=>q+1)}>+</button>
                  {[100,200,500,1000].map(v=>(
                    <button key={v} className="gt-qty-preset" onClick={()=>setQty(v)}>{v>=1000?v/1000+'k':v}</button>
                  ))}
                </div>
              </div>

              {/* LIMIT PRICE */}
              {(orderType==='LMT'||orderType==='STP LMT')&&(
                <div className="gt-field-row">
                  <span className="gt-label">Limit $</span>
                  <div className="gt-price-row">
                    <button className="gt-qty-arrow" onClick={()=>setLimitPrice(p=>fN(Math.max(0.01,parseFloat(p||0)-0.01)))}>−</button>
                    <input className="gt-input" type="number" step="0.01" value={limitPrice} onChange={e=>setLimitPrice(e.target.value)} style={{width:80}}/>
                    <button className="gt-qty-arrow" onClick={()=>setLimitPrice(p=>fN(parseFloat(p||0)+0.01))}>+</button>
                    <button className="gt-price-quick" onClick={()=>setLimitPrice(fN(bid))}>Bid</button>
                    <button className="gt-price-quick" onClick={()=>setLimitPrice(fN(ask))}>Ask</button>
                  </div>
                </div>
              )}

              {/* STOP PRICE */}
              {(orderType==='STP'||orderType==='STP LMT')&&(
                <div className="gt-field-row">
                  <span className="gt-label">Stop $</span>
                  <div className="gt-price-row">
                    <button className="gt-qty-arrow" onClick={()=>setStopPrice(p=>fN(Math.max(0.01,parseFloat(p||0)-0.01)))}>−</button>
                    <input className="gt-input" type="number" step="0.01" value={stopPrice} onChange={e=>setStopPrice(e.target.value)} style={{width:80}}/>
                    <button className="gt-qty-arrow" onClick={()=>setStopPrice(p=>fN(parseFloat(p||0)+0.01))}>+</button>
                  </div>
                </div>
              )}

              {/* TIF */}
              <div className="gt-field-row">
                <span className="gt-label">TIF</span>
                <div className="gt-type-btns">
                  {['DAY','GTC','EXT','GTX','IOC'].map(t=>(
                    <button key={t} className={`gt-type-btn ${tif===t?'active':''}`} onClick={()=>setTif(t)}>{t}</button>
                  ))}
                </div>
              </div>

              {/* BRACKET */}
              <div className="gt-field-row">
                <label className="gt-checkbox-label">
                  <input type="checkbox" checked={bracket} onChange={e=>setBracket(e.target.checked)}/>
                  Bracket Order (auto SL/TP)
                </label>
              </div>
              {bracket&&(
                <>
                  <div className="gt-field-row">
                    <span className="gt-label" style={{color:'#ef4444'}}>Stop Loss</span>
                    <input className="gt-input" type="number" step="0.01" value={slPrice} onChange={e=>setSlPrice(e.target.value)} placeholder={fN(side==='BUY'?quote.last*0.98:quote.last*1.02)}/>
                  </div>
                  <div className="gt-field-row">
                    <span className="gt-label" style={{color:'#22c55e'}}>Take Profit</span>
                    <input className="gt-input" type="number" step="0.01" value={tpPrice} onChange={e=>setTpPrice(e.target.value)} placeholder={fN(side==='BUY'?quote.last*1.06:quote.last*0.94)}/>
                  </div>
                </>
              )}

              {/* EST COST */}
              <div className="gt-est-row">
                <span>Est. {side==='BUY'?'Cost':'Proceeds'}</span>
                <span style={{color:side==='BUY'?'#ef4444':'#22c55e'}}>${fN(estCost)}</span>
              </div>
              <div className="gt-est-row">
                <span>Buying Power</span>
                <span style={{color:'#60a5fa'}}>${fN(buyPower)}</span>
              </div>

              {/* SUBMIT */}
              <div className="gt-submit-row">
                <button
                  className={`gt-submit-btn ${side==='BUY'?'buy':'sell'}`}
                  onClick={()=>placeOrder(side,orderType,parseFloat(limitPrice)||null)}
                >
                  {side==='BUY'?'▲':'▼'} {side} {qty} {symbol} {orderType==='MKT'?'@ MARKET':'@ $'+limitPrice}
                </button>
                <button className="gt-cancel-btn" onClick={cancelAll} title="Cancel All (F9 or Escape)">✕ Cancel All</button>
              </div>

              {/* QUICK ACTIONS */}
              <div className="gt-quick-row">
                <button className="gt-quick-btn buy" onClick={()=>placeOrder('BUY','MKT',null)}>Buy Mkt</button>
                <button className="gt-quick-btn buy-lmt" onClick={()=>placeOrder('BUY','LMT',bid)}>Buy Bid</button>
                <button className="gt-quick-btn buy-lmt" onClick={()=>placeOrder('BUY','LMT',ask)}>Buy Ask</button>
                <button className="gt-quick-btn sell" onClick={()=>placeOrder('SELL','MKT',null)}>Sell Mkt</button>
                <button className="gt-quick-btn sell-lmt" onClick={()=>placeOrder('SELL','LMT',ask)}>Sell Ask</button>
                <button className="gt-quick-btn sell-lmt" onClick={()=>placeOrder('SELL','LMT',bid)}>Sell Bid</button>
              </div>
            </>
          )}

          {/* POSITIONS TAB */}
          {activeTab==='positions'&&(
            <div className="gt-positions">
              <div className="gt-pos-header">
                <div className="gt-acct-row">
                  <div className="gt-acct-item"><span>Account</span><span>${fN(acctBal)}</span></div>
                  <div className="gt-acct-item"><span>Buy Power</span><span style={{color:'#60a5fa'}}>${fN(buyPower)}</span></div>
                  <div className="gt-acct-item"><span>Day P&L</span><span style={{color:todayPnL>=0?'#22c55e':'#ef4444'}}>{todayPnL>=0?'+':''}{fN(todayPnL)}</span></div>
                  <div className="gt-acct-item"><span>Open P&L</span><span style={{color:unrealized>=0?'#22c55e':'#ef4444'}}>{unrealized>=0?'+':''}{fN(unrealized)}</span></div>
                </div>
              </div>
              {positions.length===0
                ?<div className="gt-empty">No open positions</div>
                :positions.map(p=>{
                  const pnl=(quote.last-p.avg)*p.shares;
                  return(
                    <div key={p.sym} className="gt-pos-row">
                      <div className="gt-pos-sym">{p.sym}</div>
                      <div className="gt-pos-detail">
                        <span>{p.shares>0?'LONG':'SHORT'} {Math.abs(p.shares)} @ ${fN(p.avg)}</span>
                        <span style={{color:pnl>=0?'#22c55e':'#ef4444'}}>{pnl>=0?'+':''}{fN(pnl)}</span>
                      </div>
                      <button className="gt-close-pos" onClick={closePosition}>Close</button>
                    </div>
                  );
                })
              }
            </div>
          )}

          {/* ACTIVITY TAB */}
          {activeTab==='orders'&&(
            <div className="gt-activity">
              {orders.length===0
                ?<div className="gt-empty">No orders yet</div>
                :orders.map(o=>(
                  <div key={o.id} className="gt-order-row">
                    <span className={`gt-order-status ${o.status.toLowerCase()}`}>{o.status}</span>
                    <span style={{color:o.side==='BUY'?'#22c55e':'#ef4444',fontWeight:700}}>{o.side}</span>
                    <span>{o.qty} {o.sym}</span>
                    <span style={{color:'#8b949e'}}>{o.type} ${fN(o.price)}</span>
                    <span style={{color:'#484f58',fontSize:9}}>{o.time}</span>
                    {o.status==='WORKING'&&<button className="gt-cancel-order" onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,status:'CANCELLED'}:x))}>✕</button>}
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>

      {/* WORKING ORDERS BADGE */}
      {workingOrders.length>0&&(
        <div className="gt-working-badge">
          {workingOrders.length} working order{workingOrders.length>1?'s':''} · <span onClick={cancelAll} style={{cursor:'pointer',textDecoration:'underline'}}>Cancel All</span>
        </div>
      )}

      {/* TOAST */}
      {toast&&(
        <div className={`gt-toast ${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
}